import { Modal, Toast, Alert } from "react-bootstrap";
import { useState, useEffect } from "react";
import Api from "../../Auth/Api";
import { decodeJWT } from "../../Components/jwtUtils";

function ModalUploadListaPresenca({ show, onHide, onSuccess }) {
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fileSelected, setFileSelected] = useState(false);
    const [toast, setToast] = useState({});
    const [showToast, setShowToast] = useState(false);
    const [arquivoExiste, setArquivoExiste] = useState(false);
    const token = JSON.parse(localStorage.getItem("user_token"));
    const nivel = decodeJWT(token).nivel;
    const isAdmin = nivel === 'Administrador';

    // Verificar se já existe arquivo quando modal abre
    useEffect(() => {
        const verificarArquivoExistente = async () => {
            if (show && isAdmin) {
                try {
                    // Verificar se arquivo existe fazendo GET com validateStatus
                    const response = await Api.get('/api/download-lista-presenca', {
                        headers: {
                            Authorization: `Bearer ${token}`
                        },
                        responseType: 'blob',
                        validateStatus: (status) => status < 500 // Não lançar erro para 404
                    });
                    
                    if (response.status === 200) {
                        setArquivoExiste(true);
                    } else {
                        setArquivoExiste(false);
                    }
                } catch (error) {
                    // Se der erro (incluindo 404), assumir que não existe
                    setArquivoExiste(false);
                }
            } else {
                setArquivoExiste(false);
            }
        };

        verificarArquivoExistente();
    }, [show, isAdmin, token]);

    // Função para mostrar mensagens de toast
    const showToastMessage = (header, body, variant = 'error') => {
        const type = variant === 'success' ? 'toast-success' : variant === 'danger' ? 'toast-error' : 'toast-' + variant;
        setToast({ header, body, type });
        setShowToast(true);
    };

    // Função para validar tipo de arquivo
    const validarTipoArquivo = (file) => {
        const tiposPermitidos = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
            'application/vnd.ms-excel', // .xls
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
            'application/msword' // .doc
        ];
        
        const extensoesPermitidas = ['.pdf', '.xlsx', '.xls', '.docx', '.doc'];
        
        // Verificar por MIME type
        if (tiposPermitidos.includes(file.type)) {
            return true;
        }
        
        // Verificar por extensão
        const fileName = file.name.toLowerCase();
        return extensoesPermitidas.some(ext => fileName.endsWith(ext));
    };

    // Função para validar tamanho do arquivo (10MB)
    const validarTamanhoArquivo = (file) => {
        const tamanhoMaximo = 10 * 1024 * 1024; // 10MB em bytes
        return file.size <= tamanhoMaximo;
    };

    // Função para fechar o modal limpando estados
    const handleModalHide = () => {
        setShowToast(false);
        setToast({});
        setFileSelected(false);
        setIsUploading(false);
        setUploadProgress(0);
        setArquivoExiste(false);
        onHide();
    };

    // Resetar estados quando modal fecha
    const handleClose = () => {
        if (!isUploading) {
            handleModalHide();
        }
    };

    if (!isAdmin) {
        return null;
    }

    return (
        <>
            <Toast
                show={showToast}
                onClose={() => setShowToast(false)}
                delay={4000}
                autohide
                onExited={() => {
                    setToast({});
                }}
                className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
                style={{ zIndex: 1060 }}
            >
                <Toast.Header>
                    <strong className="me-auto">{toast.header}</strong>
                </Toast.Header>
                <Toast.Body>{toast.body}</Toast.Body>
            </Toast>

            <Modal show={show} backdrop="static" size="lg" onHide={handleClose} centered>
                <Modal.Header closeButton={!isUploading}>
                    <span className="fw-semibold fs-5 text-primary">
                        <i className="bi bi-cloud-upload me-2"></i>
                        Upload Lista de Presença
                    </span>
                </Modal.Header>
                <Modal.Body className="bg-gray-100 p-4">
                    <div className="mb-3">
                        <label className="form-label text-primary fw-medium mb-2">
                            Selecione o arquivo da lista de presença
                        </label>
                        <p className="text-muted small mb-3">
                            Formatos aceitos: PDF, Excel (.xlsx, .xls) ou Word (.docx, .doc)
                            <br />
                            Tamanho máximo: 10MB
                        </p>

                        {arquivoExiste && (
                            <Alert variant="warning" className="d-flex align-items-center mb-3">
                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                <div>
                                    <strong>Arquivo já existe!</strong>
                                    <br />
                                    <small>O arquivo atual será substituído pelo novo arquivo enviado.</small>
                                </div>
                            </Alert>
                        )}
                        
                        <label 
                            htmlFor="lista-presenca-upload" 
                            className={`form-control label-input ${fileSelected ? 'border-success bg-success' : 'border-primary bg-primary'} text-white fw-medium text-center cursor-pointer`}
                            style={{ cursor: isUploading ? 'not-allowed' : 'pointer', opacity: isUploading ? 0.6 : 1 }}
                        >
                            {isUploading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Enviando...
                                </>
                            ) : fileSelected ? (
                                <>
                                    <i className="bi bi-check-circle me-2"></i>
                                    Arquivo selecionado
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-cloud-upload me-2"></i>
                                    Selecionar arquivo
                                </>
                            )}
                        </label>
                        <input
                            id="lista-presenca-upload"
                            type="file"
                            accept=".pdf,.xlsx,.xls,.docx,.doc"
                            style={{ display: 'none' }}
                            disabled={isUploading}
                            onChange={async (e) => {
                                const file = e.target.files[0];
                                if (!file) {
                                    setFileSelected(false);
                                    return;
                                }
                                
                                // Validar tipo de arquivo
                                if (!validarTipoArquivo(file)) {
                                    showToastMessage('Erro!', 'Formato de arquivo não permitido. Use PDF, Excel (.xlsx, .xls) ou Word (.docx, .doc).', 'danger');
                                    e.target.value = '';
                                    setFileSelected(false);
                                    return;
                                }
                                
                                // Validar tamanho do arquivo
                                if (!validarTamanhoArquivo(file)) {
                                    showToastMessage('Erro!', 'Arquivo muito grande. O tamanho máximo é 10MB.', 'danger');
                                    e.target.value = '';
                                    setFileSelected(false);
                                    return;
                                }
                                
                                setFileSelected(true);
                                setIsUploading(true);
                                setUploadProgress(0);
                                
                                try {
                                    const formData = new FormData();
                                    formData.append('lista_presenca', file);
                                    
                                    await Api.post('/api/upload-lista-presenca', formData, {
                                        headers: {
                                            Authorization: `Bearer ${token}`,
                                            'Content-Type': 'multipart/form-data'
                                        },
                                        onUploadProgress: (progressEvent) => {
                                            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                                            setUploadProgress(percentCompleted);
                                        }
                                    });
                                    
                                    showToastMessage('Sucesso!', 'Lista de presença enviada com sucesso!', 'success');
                                    
                                    // Atualizar estado - agora existe arquivo
                                    setArquivoExiste(true);
                                    
                                    // Chamar callback de sucesso se fornecido
                                    if (onSuccess) {
                                        onSuccess();
                                    }
                                    
                                    // Fechar modal após delay
                                    setTimeout(() => {
                                        setIsUploading(false);
                                        setUploadProgress(0);
                                        setFileSelected(false);
                                        e.target.value = '';
                                        handleModalHide();
                                    }, 1500);
                                } catch (error) {
                                    console.error('Erro ao enviar lista de presença:', error);
                                    showToastMessage('Erro!', error.response?.data?.message || 'Erro ao enviar a lista de presença. Tente novamente.', 'danger');
                                    setIsUploading(false);
                                    setUploadProgress(0);
                                    setFileSelected(false);
                                    e.target.value = '';
                                }
                            }}
                        />
                        {uploadProgress > 0 && uploadProgress < 100 && (
                            <div className="progress mt-2">
                                <div 
                                    className="progress-bar" 
                                    role="progressbar" 
                                    style={{ width: `${uploadProgress}%` }}
                                    aria-valuenow={uploadProgress} 
                                    aria-valuemin="0" 
                                    aria-valuemax="100"
                                >
                                    {uploadProgress}%
                                </div>
                            </div>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer className="justify-content-center"></Modal.Footer>
            </Modal>
        </>
    );
}

export default ModalUploadListaPresenca;
